import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';

export const Testimonials = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const testimonials = [
    {
      name: 'Dr. Priya Sharma',
      role: 'Clinic Owner',
      image: 'https://images.unsplash.com/photo-1769636929231-3cd7f853d038?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0JTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc1MDc5Mjg3fDA&ixlib=rb-4.1.0&q=85',
      text: 'Nexus transformed our online presence. Patient bookings increased by 85% in just 3 months. The website is beautiful and incredibly easy to use.',
      rating: 5,
    },
    {
      name: 'Rahul Mehta',
      role: 'Café Owner',
      image: 'https://images.unsplash.com/photo-1766022411633-e88e3650538b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0JTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc1MDc5Mjg3fDA&ixlib=rb-4.1.0&q=85',
      text: 'Working with Nexus was an absolute pleasure. They understood our vision and delivered a stunning website that perfectly captures our café\'s vibe.',
      rating: 5,
    },
    {
      name: 'Sarah Johnson',
      role: 'Startup Founder',
      image: 'https://images.unsplash.com/photo-1765776830139-72b2184dae5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0JTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc1MDc5Mjg3fDA&ixlib=rb-4.1.0&q=85',
      text: 'The team at Nexus is incredibly talented. They built our SaaS platform from scratch and it\'s been performing flawlessly. Highly recommend!',
      rating: 5,
    },
  ];

  const scrollTo = useCallback(
    (index) => {
      if (emblaApi) emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <section id="testimonials" className="py-24 md:py-32 relative" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#007AFF] mb-4 block" data-testid="testimonials-label">
            Client Stories
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="testimonials-title">
            What Our Clients{' '}
            <span className="bg-gradient-to-r from-[#007AFF] to-[#00F0FF] bg-clip-text text-transparent">
              Say About Us
            </span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto leading-relaxed" data-testid="testimonials-description">
            Don't just take our word for it. Here's what our clients have to say about working with us.
          </p>
        </motion.div>

        <div className="overflow-hidden" ref={emblaRef} data-testid="testimonials-carousel">
          <div className="flex">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="flex-[0_0_100%] min-w-0 px-4" data-testid={`testimonial-slide-${index}`}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="glass-card p-8 md:p-12 rounded-2xl max-w-4xl mx-auto"
                >
                  <div className="flex gap-1 mb-6">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#007AFF] text-[#007AFF]" />
                    ))}
                  </div>
                  <p className="text-xl md:text-2xl text-zinc-300 leading-relaxed mb-8" data-testid={`testimonial-text-${index}`}>
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center gap-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-[#007AFF]"
                      data-testid={`testimonial-avatar-${index}`}
                    />
                    <div>
                      <div className="font-semibold text-lg" data-testid={`testimonial-name-${index}`}>
                        {testimonial.name}
                      </div>
                      <div className="text-zinc-500" data-testid={`testimonial-role-${index}`}>
                        {testimonial.role}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-center gap-2 mt-8">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollTo(index)}
              className={`w-2 h-2 rounded-full transition-all cursor-hover ${
                index === selectedIndex ? 'w-8 bg-[#007AFF]' : 'bg-zinc-700'
              }`}
              data-testid={`carousel-dot-${index}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};